import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { useAuth } from '../contexts/AuthContext';
import { CREDIT_PACKAGES } from '@photofeeler/shared';
import { JsonLd } from '../components/JsonLd';

const FAQ_ITEMS = [
  {
    q: 'What does 1 credit get me?',
    a: 'One photo analyzed by all 3 AI models with scores, traits, and suggestions.',
  },
  { q: 'What if analysis fails?', a: 'Credit is automatically refunded. Zero risk.' },
  { q: 'Do credits expire?', a: 'No. They never expire.' },
] as const;

export function Purchase() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  useEffect(() => {
    import('../lib/analytics').then(({ trackPurchasePageViewed }) => trackPurchasePageViewed());
  }, []);
  usePageMetadata({
    title: 'Purchase Credits — PhotoFeeler AI',
    description:
      'Buy credits for deeper Dating and LinkedIn profile analysis. Credits apply instantly and never expire.',
    canonicalPath: '/purchase',
  });

  const faqJsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_ITEMS.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.a,
        },
      })),
    }),
    [],
  );

  const productJsonLd = useMemo(
    () =>
      (['starter', 'standard', 'pro'] as const)
        .map((id) => CREDIT_PACKAGES.find((p) => p.id === id)!)
        .filter(Boolean)
        .map((pack) => ({
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: `PhotoFeeler AI ${pack.label} Credits`,
          description: `${pack.credits} AI photo analysis credits for dating and LinkedIn profile optimization.`,
          brand: { '@type': 'Organization', name: 'PhotoFeeler AI' },
          offers: {
            '@type': 'Offer',
            price: (pack.price / 100).toFixed(2),
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
            url: 'https://photofeeler.ai/purchase',
          },
        })),
    [],
  );

  const fmt = (cents: number) => (cents / 100).toFixed(2);

  if (!isAuthenticated)
    return (
      <div className="pt-28 pb-16 text-center max-w-4xl mx-auto px-5 sm:px-8 animate-fade-in">
        <div className="card max-w-sm mx-auto">
          <h2 className="font-sans text-xl text-ink mb-3">Sign in required</h2>
          <p className="text-sm text-ink-faint mb-6" style={{ fontWeight: 300 }}>
            Please sign in to purchase credits.
          </p>
          <button onClick={() => navigate('/')} className="btn-primary text-xs">
            Sign in
          </button>
        </div>
      </div>
    );

  // Order: Starter ($5) → Standard ($10) → Pro ($20)
  const orderedPacks = (['starter', 'standard', 'pro'] as const)
    .map(id => CREDIT_PACKAGES.find(p => p.id === id)!)
    .filter(Boolean);

  return (
    <div className="relative overflow-hidden pt-28 pb-16 animate-fade-in max-w-5xl mx-auto px-5 sm:px-8">
      <JsonLd data={faqJsonLd} />
      {productJsonLd.map((data, i) => (
        <JsonLd key={i} data={data} />
      ))}
      <div className="page-orb left-[-5rem] top-28 h-72 w-72 bg-rose/15" />
      <div className="page-orb right-[-5rem] top-24 h-80 w-80 bg-info/10" />

      <div className="page-hero-panel text-center mb-10">
        <div className="premium-kicker mb-3">Credits</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink mb-3" style={{ fontWeight: 600 }}>
          Keep testing until you have a winner
        </h1>
        <p className="hero-copy max-w-2xl mx-auto">
          Use credits to compare options, retake with better direction, and keep improving until the right photo is obvious.
        </p>
        <div className="grid sm:grid-cols-3 gap-3 mt-6">
          <div className="premium-stat">
            <div className="text-sm font-sans text-ink" style={{ fontWeight: 600 }}>Credits never expire</div>
            <div className="text-xs text-ink-faint mt-1">Use them whenever you&apos;re ready</div>
          </div>
          <div className="premium-stat">
            <div className="text-sm font-sans text-ink" style={{ fontWeight: 600 }}>Private by default</div>
            <div className="text-xs text-ink-faint mt-1">Photos are never shared or stored long-term</div>
          </div>
          <div className="premium-stat">
            <div className="text-sm font-sans text-ink" style={{ fontWeight: 600 }}>Refund on failed analysis</div>
            <div className="text-xs text-ink-faint mt-1">If analysis fails, credit is returned automatically</div>
          </div>
        </div>
      </div>

      <div className="card max-w-2xl mx-auto mb-8 text-center">
        <div className="text-sm font-sans text-ink leading-relaxed" style={{ fontWeight: 500 }}>
          Free during beta &mdash; credits are complimentary while we refine the product.
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5 max-w-2xl mx-auto mb-12">
        {orderedPacks.map((pack) => {
          const isPro = 'popular' in pack && pack.popular;
          return (
            <div
              key={pack.id}
              className={`transition-all duration-500 lift-hover ${isPro ? 'card-rose' : 'card'}`}
            >
              {isPro && (
                <div className="text-[10px] font-sans font-medium text-rose mb-3">Best value</div>
              )}
              <div className="text-xs font-sans text-ink-faint tracking-wider uppercase mb-2">
                {pack.label}
              </div>
              <div className="text-3xl font-sans text-ink mb-1 line-through opacity-50" style={{ fontWeight: 600 }}>
                ${fmt(pack.price)}
              </div>
              <div className="text-xs text-ink-faint mb-5">
                {pack.credits} credits &middot; free during beta
              </div>
              <button
                onClick={() => navigate('/app')}
                className={`w-full text-xs ${isPro ? 'btn-rose' : 'btn-primary'}`}
              >
                Credits are free during beta
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-20 max-w-lg mx-auto card">
        <h3 className="text-xs font-sans font-medium text-ink-faint tracking-wider uppercase text-center mb-6">
          Common questions
        </h3>
        <div className="space-y-4">
          {FAQ_ITEMS.map(({ q, a }) => (
            <div key={q} className="border-b border-surface-border pb-4">
              <div className="text-sm font-sans font-medium text-ink mb-1">{q}</div>
              <div className="text-sm text-ink-faint" style={{ fontWeight: 300 }}>
                {a}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
